import type { IconName } from "@aeroglobe/proxima-design-system";
import type { PolicyFormNode } from "../types/policyForm";

const POLICY_TAB_LAYOUT_SECTION_KEYS = new Set([
  "POLICY_DETAILS",
  "ASSIGNMENTS",
]);

const POLICY_RULES_LAYOUT_SECTION_KEY = "RULES";

const SECTION_ICONS: Record<string, IconName> = {
  POLICY_DETAILS: "m-contract",
  ASSIGNMENTS: "m-group-icon",
  RULES: "m-penalty",
  RULE_CARD: "m-analytics",
  BUDGET: "m-balance",
  CABIN: "m-issue-ticket",
  AIRLINE: "m-airlines",
  BOOKING: "m-flight",
  ADVANCE_BOOKING: "m-flight-departure-icon",
  CAPACITY: "m-baggage",
  COMPLIANCE: "m-receipt-long",
};

export function getSectionIcon(key: string): IconName {
  return SECTION_ICONS[key] ?? "m-info";
}

export function isPolicyRuleSection(sectionKey: string): boolean {
  return sectionKey === "RULE_CARD" || sectionKey.startsWith("RULE_CARD__");
}

export function isPolicyTabLayoutSection(sectionKey: string): boolean {
  return POLICY_TAB_LAYOUT_SECTION_KEYS.has(sectionKey);
}

export function isPolicyRulesLayoutSection(sectionKey: string): boolean {
  return sectionKey === POLICY_RULES_LAYOUT_SECTION_KEY;
}

export function partitionPolicyLayoutNodes<T extends PolicyFormNode>(
  nodes: T[],
): {
  policyTabNodes: T[];
  rulesNodes: T[];
} {
  const policyTabNodes: T[] = [];
  const rulesNodes: T[] = [];

  nodes.forEach((node) => {
    if (node.kind === "ruleCategory") {
      rulesNodes.push(node);
      return;
    }
    policyTabNodes.push(node);
  });

  return { policyTabNodes, rulesNodes };
}

export const POLICY_FORM_TAB_POLICY = "policy";

export function policyTabKeyFromIndex(
  index: number,
  ruleCategoryKeys: string[],
): string {
  if (index <= 0) {
    return POLICY_FORM_TAB_POLICY;
  }

  return ruleCategoryKeys[index - 1] ?? POLICY_FORM_TAB_POLICY;
}

export function policyTabIndexFromKey(
  tabKey: string,
  ruleCategoryKeys: string[],
): number {
  if (!tabKey || tabKey === POLICY_FORM_TAB_POLICY) {
    return 0;
  }

  const categoryIndex = ruleCategoryKeys.indexOf(tabKey);
  return categoryIndex === -1 ? 0 : categoryIndex + 1;
}

export function normalizePolicyContentMode(mode: unknown): "view" | "edit" {
  return mode === "edit" ? "edit" : "view";
}

export { applyBuilderDefaultsToFormData } from "./applyBuilderDefaultsToFormData";
export {
  walkFormNodes,
  findFormNode,
  findConditionFieldsByCategory,
  findActiveConditionFieldsForCategory,
  extractRuleCategoryNodes,
  collectApiOptionSources,
  type WalkFormNodesOptions,
} from "./walkFormNodes";

export {
  getFormStateValue,
  setFormStateValue,
  joinFormStatePath,
  joinFormDataPath,
  indexFormStatePath,
  joinFormStatePathSegments,
  formStatePath,
  getRuleCategoryScope,
} from "./formPaths";
